import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Paginator, Sort } from '@app/classes';
import { PdfViewerComponent } from '@app/components';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { JobFunnel } from '@app/models/recruiting/job-funnel.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { Report } from '@app/modules/reporting/services/queries/report';
import { FileHelperService } from '@app/services';
import { ApplicantAttachment } from '@models/recruiting/applicant-attachments.model';
import { JobAttachment } from '@models/recruiting/job-attachments.model';
import { PageContext } from '../meta/view-contexts';

@Injectable()
export class ApplicantService {
    pageContext: PageContext = 'index';
    indexPaginator: Paginator;

    constructor(
        private fileHelper: FileHelperService,
        private http: HttpClient
    ) {}

    updateContext(context: PageContext): void {
        this.pageContext = context;
    }

    async getApplicantAttachments(applicantId: number): Promise<ApplicantAttachment[]> {
        const [applicantAttachments] = await ApplicantAttachment.where('applicantId', applicantId)
            .with(['jobAttachment', 'file'])
            .orderBy('jobAttachmentIdAndCreatedAt', 'DESC')
            .get();

        return applicantAttachments;
    }

    async getJobAttachments(jobPostingId: number): Promise<JobAttachment[]> {
        const [jobAttachments] = await JobAttachment.with(['jobAttachmentType']).where('atsJobId', jobPostingId).get();

        return jobAttachments;
    }

    async previewDocument(
        applicantAttachment: ApplicantAttachment,
        pdfViewer: PdfViewerComponent,
        index: number,
        pdfPreviewersStates: { panelState: boolean; pdfPreviewableError: boolean; pdfError: boolean }[],
        pdfException = 'InvalidPDFException'
    ): Promise<void> {
        pdfViewer.statusChange.subscribe((event: { loading: boolean; error: any }) => {
            if (event.error === null) {
                pdfPreviewersStates[index].pdfError = false;
                pdfPreviewersStates[index].pdfPreviewableError = false;
                return;
            }

            event.error.name === pdfException
                ? (pdfPreviewersStates[index].pdfPreviewableError = true)
                : (pdfPreviewersStates[index].pdfError = true);
        });

        await pdfViewer.setSource(
            this.fileHelper.path(
                applicantAttachment.convertedFileId ? applicantAttachment.convertedFileId : applicantAttachment.fileId
            )
        );
    }

    async moveApplicant(applicant: Applicant, jobPosting: JobPosting, jobFunnel: JobFunnel): Promise<void> {
        const url = `/v2/applicantTracker/applicants/${applicant.id}/move`;

        const payload = {
            data: {
                type: 'applicants',
                attributes: {
                    atsJobId: jobPosting.id,
                    atsJobFunnelId: jobFunnel.id,
                },
            },
        };

        await this.http.put(url, payload).toPromise();
    }

    async copyApplicant(applicant: Applicant, jobPosting: JobPosting, jobFunnel: JobFunnel): Promise<Applicant> {
        const url = `/v2/applicantTracker/applicants/${applicant.id}/copy`;

        const payload = {
            data: {
                type: 'applicants',
                attributes: {
                    atsJobId: jobPosting.id,
                    atsJobFunnelId: jobFunnel.id,
                },
            },
        };

        const response = await this.http
            .post(url, payload)
            .toPromise()
            .catch((error) => {
                throw error;
            });

        return new Applicant(response['data']['attributes']);
    }

    async exportApplicants(
        format: 'csv' | 'xlsx',
        searchPhrase?: string,
        filters?: { [key: string]: any[] },
        sort?: Sort
    ): Promise<void> {
        const report = new Report(this.fileHelper);
        const reportFilters = [];

        for (const name of Object.keys(filters)) {
            reportFilters.push({
                name,
                value: filters[name],
            });
        }

        reportFilters.push({
            name: 'query',
            value: searchPhrase,
        });

        report.name('applicantReport').filters(reportFilters).sort(sort.column, sort.order);

        await report.export(format);
    }
}
